import { defineStore } from 'pinia'
import api, { getUploadSession } from '../api'
import { DateTime } from 'luxon'
import { useNotificationStore } from './notifications'
import Str from 'laravel-js-str'
import routeData from '@/routes.json'

export const useSystemStore = defineStore('system', {
  state: () => ({
    routes: routeData,
    error: null,
    enums: null,
    enumMappings: null,
    activeUpload: null,
    darkMode: false,
    reducedMotion: false,
    confirmExitMethods: [],
    scopes: [],
    selectedScope: null,
    titleOverride: null,
    introOverride: null,
    dynamicTitleEndpoint: null,
    dynamicTitle: null,
    chartFont: 'Montserrat',
    chartColours: [
      '#309EAA',
      '#AB6430',
      '#30AB72',
      '#AB8330',
      '#39666B',
      '#564B35',
      '#355647',
      '#564335',
      '#283233',
      '#2C3330'
    ],
    navPermissions: []
  }),
  getters: {
    systemInitialized() {
      return this.enums !== null
    }
  },
  actions: {
    toggleDarkMode(value = null) {
      this.darkMode = value !== null ? value : !this.darkMode

      document.documentElement.classList.toggle('dark', this.darkMode)
    },
    toggleUiAnimations(value = null) {
      this.reducedMotion = value !== null ? value : !this.reducedMotion

      document.documentElement.classList.toggle('animation-disabled', this.reducedMotion)
    },
    async bootSystem() {
      const notificationStore = useNotificationStore()
      notificationStore.initLaravelEcho()
      await Promise.all([this.getEnums(), this.getScopes()])
    },
    async getEnums() {
      const response = await api.get(`/v3/utils/enums`)
      this.enums = response.data.enums
      this.enumMappings = response.data.mappings
    },
    async getUploadSession() {
      if (this.uploadSession && DateTime.fromISO(this.uploadSession.expires) > DateTime.utc()) {
        return this.uploadSession
      }

      this.uploadSession = await getUploadSession()
      return this.uploadSession
    },
    registerConfirmExitMethod(method) {
      this.confirmExitMethods.push(method)
    },
    unregisterConfirmExitMethod(method) {
      const index = this.confirmExitMethods.indexOf(method)
      if (index >= 0) {
        this.confirmExitMethods.splice(index, 1)
      }
    },
    async getScopes() {
      const response = await api.get('v3/admin-hub/scopes')
      let scopes = []

      for (let n = 0; n < response.data.networks.length; n++) {
        const networkId = response.data.networks[n].id
        scopes.push({
          id: { network_id: networkId, client_id: null, office_id: null },
          name: response.data.networks[n].name,
          label: 'Network'
        })
        for (let c = 0; c < response.data.networks[n].clients.length; c++) {
          const clientId = response.data.networks[n].clients[c].id
          scopes.push({
            id: { network_id: networkId, client_id: clientId, office_id: null },
            name: response.data.networks[n].clients[c].name,
            label: 'Client'
          })
          for (let o = 0; o < response.data.networks[n].clients[c].offices.length; o++) {
            const officeId = response.data.networks[n].clients[c].offices[o].id
            scopes.push({
              id: { network_id: networkId, client_id: clientId, office_id: officeId },
              name: response.data.networks[n].clients[c].offices[o].name,
              label: 'Office'
            })
          }
        }
      }

      scopes.sort((a, b) => a.name > b.name)

      this.scopes = scopes
    },
    setScope(scope) {
      this.selectedScope = scope
    },
    flattenRoutes(routes, parentId) {
      let output = []

      for (let path in routes) {
        output.push({
          parent_id: parentId,
          path,
          ...routes[path]
        })

        output = output.concat(this.flattenRoutes(routes[path].children, routes[path].id))
      }

      return output
    },
    rebuildRoutes(router, pageView) {
      this.routes = this.flattenRoutes(this.routes, null)

      let routesToKeep = []
      let tabPages = []

      // Rebuild the routes from the database
      for (let i = 0; i < this.routes.length; i++) {
        if (this.routes[i].is_external) {
          continue
        }

        const parentPage = this.routes.find((r) => r.id === this.routes[i].parent_id)
        const pageModel = this.routes[i].modules.find((m) => m.model !== null)
        if (parentPage && parentPage.show_tabs && !pageModel) {
          const name = `page.${this.routes[i].id}`
          routesToKeep.push(name)
          tabPages.push({
            parent: `page.${this.routes[i].parent_id}`,
            route: {
              name,
              path: this.routes[i].path,
              component: pageView,
              meta: this.routes[i]
            }
          })
          continue
        }

        let route = this.routes[i]
        let path = []
        do {
          let localPath = route.path
          const datatype = route.param_datatype || 'int'
          const paramRegex = datatype === 'int' ? '(\\d+)' : ''
          const component = route.modules.find((m) => m.model !== null)
          if (component) {
            let key = component.model.split('\\')
            key = Str.camel(key[key.length - 1])
            if (Str.contains(path.join('/'), `:${key}${paramRegex}/`)) {
              route = this.routes.find((r) => r.id === route.parent_id)
              continue
            }
            localPath = `:${key}${paramRegex}/${localPath}`
          }
          path.unshift(localPath)
          route = this.routes.find((r) => r.id === route.parent_id)
        } while (route)
        path = path.join('/')

        const name = `page.${this.routes[i].id}`
        routesToKeep.push(name)
        router.addRoute({
          name,
          path: Str.start(path, '/'),
          component: pageView,
          meta: this.routes[i]
        })
      }

      for (let i = 0; i < tabPages.length; i++) {
        router.addRoute(tabPages[i].parent, tabPages[i].route)
      }

      // Remove old routes
      let existingRoutes = router.getRoutes()
      for (let i = 0; i < existingRoutes.length; i++) {
        if (existingRoutes[i].name.indexOf('page') !== 0) {
          continue
        }

        if (routesToKeep.includes(existingRoutes[i].name)) {
          continue
        }

        router.removeRoute(existingRoutes[i].name)
      }

      router.replace(router.currentRoute.value.fullPath)
    },
    overridePageTitle(title = null, intro = null) {
      this.titleOverride = title
      this.introOverride = intro

      if (title) {
        document.title = title ? `${title} - Nurtur Admin Hub` : 'Nurtur Admin Hub'
      }
    },
    async setDynamicTitle(key, endpoint, attribute, refresh = false) {
      if (!refresh && endpoint === this.dynamicTitleEndpoint) {
        this.overridePageTitle(this.dynamicTitle)
        return
      }

      this.dynamicTitleEndpoint = endpoint
      this.titleOverride = ' '
      try {
        const response = await api.get(endpoint)

        this.overridePageTitle(
          (attribute instanceof Function ? attribute(response.data) : response.data[attribute]) || null
        )
        this.dynamicTitle = this.titleOverride.toString()

        if (['network', 'office'].includes(key)) {
          this.setNavPermissions(response.data.permissions_list || response.data.all_permissions_list || [])
        }
      } catch (e) {
        console.log(e)
        this.overridePageTitle()
      }
    },
    setNavPermissions(permissions) {
      this.navPermissions = permissions
    }
  }
})
